import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { environment } from '@env/environment';

import {
  AuthGrantType,
  AuthMethod,
  AuthTokenRequest,
  AuthTokenResponse,
  authURLs,
  BasicCredentials,
  CodeState,
  SocialCredentials,
} from '@personal-area/global/authorization/models';
import { IS_API_GATEWAY, IS_PRIVATE_API } from '@personal-area/global/http/models';
import { Role } from '@personal-area/global/users/models';

import { map, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OauthApiService {
  private readonly _stateString = environment.apiData.stateString;
  private readonly _clientId = environment.apiData.clientID;
  private readonly _clientSecret = environment.apiData.clientSecret;
  private _http = inject(HttpClient);

  public requestCode(context: HttpContext): Observable<string> {
    return this._http
      .get<CodeState>(authURLs.code(this._clientId, this._stateString), {
        context: context.set(IS_API_GATEWAY, true),
      })
      .pipe(
        switchMap((resp: CodeState) => {
          // invalid state error
          if (resp.state !== this._stateString) {
            return throwError(() => 'API error: invalid state returned');
          }

          return of(resp.code);
        }),
      );
  }

  public requestTokenV2(
    method: AuthMethod,
    context: HttpContext,
    headers: HttpHeaders,
    code: string,
    basicCredentials?: BasicCredentials,
    socialCredentials?: SocialCredentials,
  ): Observable<AuthTokenResponse> {
    const requestBody: AuthTokenRequest = {
      grantType: AuthGrantType.AuthorizationCode,
      clientID: this._clientId,
      clientSecret: this._clientSecret,
      code,
      method,
      basicCredentials,
      socialCredentials,
    };

    return this._http.post<AuthTokenResponse>(
      authURLs.tokenV2,
      requestBody,
      { headers, context },
    );
  }

  public requestRefreshToken(
    refreshToken: string,
  ): Observable<AuthTokenResponse> {
    const context = new HttpContext()
      .set(IS_API_GATEWAY, true)
      .set(IS_PRIVATE_API, true);

    return this._http.post<AuthTokenResponse>(
      authURLs.tokenV2,
      {
        grantType: AuthGrantType.RefreshToken,
        clientID: this._clientId,
        clientSecret: this._clientSecret,
        refreshToken,
      },
      { context },
    );
  }

  public getUsedAccountTypes(userId: number): Observable<Role[]> {
    return this._http
      .get<{ items: Role[] }>(authURLs.usedAccountTypes(userId), {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      })
      .pipe(map((data) => data.items || []));
  }
}
